/* eslint-disable no-inner-declarations */
import {useCallback, useEffect, useRef} from 'react';

import {Control, controlsUtils, FabricObject, LayoutManager, Point, Textbox, util} from 'fabric';
import {usePlayerStore} from 'module/player';
import {useSheet} from 'module/sheet/useSheet';
import {useTextStore} from 'module/text';
import {useSnackbarStore} from 'store';

import {buildArrowChildren,calcArrowAngle} from './Drawer/Line';
import {useAction} from './canvas-actions';

// controls distribution to elements
const editControlElement = ['player', 'text'];

// No-op layout manager for arrow groups — prevents auto-recalculation of group bounds
export class ArrowLayoutManager extends LayoutManager {
    performLayout() {}
}

// Sync custom scene coords after rotation or modification
export const syncCustomCoords = (target) => {
    if (!target?.custom) return;

    if (target.custom.type === 'line') {
        const line = target._objects?.[0];
        if (!line) return;
        const mat = line.calcTransformMatrix();
        const pts = line.calcLinePoints();
        const p1 = new Point(pts.x1, pts.y1).transform(mat);
        const p2 = new Point(pts.x2, pts.y2).transform(mat);
        target.custom.x1 = p1.x; target.custom.y1 = p1.y;
        target.custom.x2 = p2.x; target.custom.y2 = p2.y;
    }

    if (target.custom.type === 'triangle') {
        const pts = target.points;
        const mat = target.calcTransformMatrix();
        const po = target.pathOffset;
        const toScene = (p) => new Point(p.x - po.x, p.y - po.y).transform(mat);
        const a = toScene(pts[0]), b = toScene(pts[1]), c = toScene(pts[2]);
        target.custom.ax = a.x; target.custom.ay = a.y;
        target.custom.bx = b.x; target.custom.by = b.y;
        target.custom.cx = c.x; target.custom.cy = c.y;
    }
};

// controls
const customControls = ['editControl', 'deleteControl', 'tipControl', 'tailControl', 'cornerA', 'cornerB', 'cornerC'];

// Mutate the arrow group's internal line+triangle in place with new absolute endpoints
// (kept for external use if needed)
export const updateArrowEndpoints = (group, x1, y1, x2, y2) => {
    const line = group._objects[0];
    const triangle = group._objects[1];
    const invMatrix = util.invertTransform(group.calcTransformMatrix());
    const local1 = new Point(x1, y1).transform(invMatrix);
    const local2 = new Point(x2, y2).transform(invMatrix);
    line.set({x1: local1.x, y1: local1.y, x2: local2.x, y2: local2.y});
    triangle.set({
        left: local2.x,
        top: local2.y,
        angle: calcArrowAngle(local1.x, local1.y, local2.x, local2.y) + 90,
    });
    group.set('dirty', true);
    group.canvas?.requestRenderAll();
};

export const setGroupControlVisibility = (group) => {
    // hide default controls including scale button for multiple selections
    const hideDefaultControls = {
        mtr: false,
        tl: false,
        mt: false,
        tr: false,
        ml: false,
        mr: false,
        bl: false,
        mb: false,
        br: false, // Hide scale button for multiple selections
    };
    
    // Set border properties for group selection
    group.set({
        hasBorders: true,
        borderColor: 'rgba(255,255,255,0.5)',
        borderScaleFactor: 0.5,
        borderDashArray: null,
        padding: 3,
        transparentCorners: false,
        cornerColor: '#000',
        selectionBackgroundColor: 'rgba(0,0,0,0.15)',
        backgroundColor: 'rgba(255,0,0,0.5)',
    });
    
    // Remove borders from individual objects but keep selection background
    if (group._objects && Array.isArray(group._objects)) {
        group._objects.forEach(obj => {
            obj.set({
                hasBorders: false,
            });
        });
    }
    
    group.setControlsVisibility(hideDefaultControls);
};

export const customControlVisibility = (element) => {
    // CRITICAL: Ensure the element has the custom controls from the prototype
    // In Fabric v6, instances might not inherit prototype controls properly
    if (!element.controls.editControl && FabricObject.prototype.controls.editControl) {
        element.controls.editControl = FabricObject.prototype.controls.editControl;
    }

    if (!element.controls.deleteControl && FabricObject.prototype.controls.deleteControl) {
        element.controls.deleteControl = FabricObject.prototype.controls.deleteControl;
    }

    if (!element.controls.tipControl && FabricObject.prototype.controls.tipControl) {
        element.controls.tipControl = FabricObject.prototype.controls.tipControl;
    }

    if (!element.controls.tailControl && FabricObject.prototype.controls.tailControl) {
        element.controls.tailControl = FabricObject.prototype.controls.tailControl;
    }

    for (const key of ['cornerA', 'cornerB', 'cornerC']) {
        if (!element.controls[key] && FabricObject.prototype.controls[key]) {
            element.controls[key] = FabricObject.prototype.controls[key];
        }
    }

    // CRITICAL: Ensure the element has the mtr control from the prototype
    if (!element.controls.mtr && FabricObject.prototype.controls.mtr) {
        element.controls.mtr = FabricObject.prototype.controls.mtr;
    }

    // hide default controls
    // true = visible, false = hidden
    const hideDefaultControls = {
        mtr: true,
        tl: false,
        mt: false,
        tr: false,
        ml: false,
        mr: false,
        bl: false,
        mb: false,
        br: false,
        editControl: true,
        deleteControl: true,
        tipControl: false,
        tailControl: false,
        cornerA: false,
        cornerB: false,
        cornerC: false,
    };

    // add text type proper controls
    // @Warning can't use directly
    // if(element.custom.type === 'text')
    // as somehow its interfering pencil functionality and make it stop
    const custom = {...element.custom};

    // allow controls
    if(custom.type === 'text') {
        // allow text box resize
        hideDefaultControls.mr = true;
        // allow scale control for textbox
        hideDefaultControls.br = true;

        // mr — circle handle like arrow endpoints (width resize)
        if (element.controls?.mr) {
            element.controls.mr.render = function (ctx, left, top) {
                const size = 14;
                ctx.save();
                ctx.translate(left, top);
                ctx.beginPath();
                ctx.arc(0, 0, size / 2, 0, 2 * Math.PI);
                ctx.fillStyle = 'white';
                ctx.fill();
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 2;
                ctx.stroke();
                ctx.restore();
            };

            element.controls.mr.cursorStyleHandler = (_, __, fabricObject) => {
                const cursors = ['ew-resize', 'nesw-resize', 'ns-resize', 'nwse-resize', 'ew-resize', 'nesw-resize', 'ns-resize', 'nwse-resize'];
                const n = Math.round(((fabricObject.angle % 360) + 360) % 360 / 45) % 8;
                return cursors[n];
            };

            element.controls.mr.sizeX = 28;
            element.controls.mr.sizeY = 28;
        }

        // br — corner bracket handle (scale)
        if (element.controls?.br) {
            element.controls.br.render = function (ctx, left, top, _, fabricObject) {
                const arm = 8;
                const angle = (fabricObject.angle || 0) * Math.PI / 180;
                ctx.save();
                ctx.translate(left, top);
                ctx.rotate(angle);
                ctx.strokeStyle = 'white';
                ctx.lineWidth = 2.5;
                ctx.lineCap = 'square';
                ctx.beginPath();
                ctx.moveTo(-arm, 0);
                ctx.lineTo(0, 0);
                ctx.lineTo(0, -arm);
                ctx.stroke();
                ctx.restore();
            };

            element.controls.br.cursorStyleHandler = (_, __, fabricObject) => {
                const cursors = ['nwse-resize', 'n-resize', 'nesw-resize', 'e-resize', 'nwse-resize', 's-resize', 'nesw-resize', 'w-resize'];
                const n = Math.round(((fabricObject.angle % 360) + 360) % 360 / 45) % 8;
                return cursors[n];
            };

            element.controls.br.sizeX = 28;
            element.controls.br.sizeY = 28;
        }
    }

    // show endpoint controls for arrows, hide edit/rotate for them
    if (custom.type === 'line') {
        hideDefaultControls.tipControl = true;
        hideDefaultControls.tailControl = true;
        hideDefaultControls.editControl = false;
        hideDefaultControls.mtr = false;
    }

    // show corner controls for triangles, hide edit/rotate for them
    if (custom.type === 'triangle') {
        hideDefaultControls.cornerA = true;
        hideDefaultControls.cornerB = true;
        hideDefaultControls.cornerC = true;
        hideDefaultControls.editControl = false;
        hideDefaultControls.mtr = false;

        // Draw selection highlight following the triangle shape
        element.drawSelectionBackground = function(ctx) {
            if ((this.canvas?._activeObject) !== this) return;
            const pts = this.points;
            const po = this.pathOffset;
            ctx.save();
            ctx.transform(...this.calcTransformMatrix());
            ctx.fillStyle = 'rgba(0,0,0,0.15)';
            ctx.beginPath();
            ctx.moveTo(pts[0].x - po.x, pts[0].y - po.y);
            ctx.lineTo(pts[1].x - po.x, pts[1].y - po.y);
            ctx.lineTo(pts[2].x - po.x, pts[2].y - po.y);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
        };
    }

    // Set border properties on the element itself
    element.set({
        hasBorders: custom.type === 'triangle' ? false : true,
        borderColor: 'rgba(255,255,255,0.5)',
        borderScaleFactor: 0.5,
        borderDashArray: null,
        padding: 3,
        transparentCorners: false,
        cornerColor: '#000',
        selectionBackgroundColor: custom.type === 'triangle' ? '' : 'rgba(0,0,0,0.15)',
    });
    
    // Safely set mtr control properties if it exists - position at top middle
    if (element.controls?.mtr) {
        const noRotate = custom.type === 'line' || custom.type === 'triangle';

        if (noRotate) {
            // Clone the control so we don't mutate the prototype
            element.controls.mtr = Object.assign(Object.create(Object.getPrototypeOf(element.controls.mtr)), element.controls.mtr);
            element.controls.mtr.visible = false;
            element.controls.mtr.getVisibility = () => false;
        } else {
            // Ensure the element has the updated render function from the prototype
            if (FabricObject.prototype.controls.mtr?.render) {
                element.controls.mtr.render = FabricObject.prototype.controls.mtr.render;
            }

            if (FabricObject.prototype.controls.mtr?.getVisibility) {
                element.controls.mtr.getVisibility = FabricObject.prototype.controls.mtr.getVisibility;
            }

            element.controls.mtr.x = 0;
            element.controls.mtr.y = -0.5;
            element.controls.mtr.offsetY = -40;
            element.controls.mtr.offsetX = 0;

            element.controls.mtr.visible = true;
        }
    }

    element.setControlsVisibility(hideDefaultControls);

    // allow our custom controls to visible
    element.hasControls = true;
};

const editIcon =
    'data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style="fill:white;"><circle style="fill:black;" class="st0" cx="256" cy="256" r="256"/>\n' +
    '<path d="M194.9,367.8l-47.4,9.6c-7.7,1.6-14.4-5.2-12.8-12.8l9.6-47.4c1.2-6.1,4.2-11.7,8.7-16.1l7.3-7.2l58.1,58.1l-7.3,7.3\n' +
    '\tC206.6,363.5,201,366.5,194.9,367.8z"/>\n' +
    '<path d="M380.7,189.5l-134,134l-58.1-58.1l134-134c7.3-7.3,19.2-7.3,26.6,0l31.6,31.6C388,170.2,388,182.1,380.7,189.5z"/></svg>';

const deleteIcon =
    'data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style="fill:white;"><path d="M256,0c141.4,0,256,114.6,256,256S397.4,512,256,512S0,397.4,0,256S114.6,0,256,0z" style="fill:black;"/>\n' +
    '<path d="M175,175c9.4-9.4,24.6-9.4,33.9,0l47,47l47-47c9.4-9.4,24.6-9.4,33.9,0s9.4,24.6,0,33.9l-47,47l47,47\n' +
    '\tc9.4,9.4,9.4,24.6,0,33.9s-24.6,9.4-33.9,0l-47-47l-47,47c-9.4,9.4-24.6,9.4-33.9,0s-9.4-24.6,0-33.9l47-47l-47-47\n' +
    '\tC165.6,199.5,165.6,184.3,175,175z"/></svg>';

const rotateIcon = `data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 100 100">
<circle cx="50" cy="50" r="50" style="fill:black;"/>
<path style="fill:white;" d="M66.2,34.1c-4.1-4.1-9.7-6.4-15.5-6.6l.8-1c1.1-1.3.9-3.2-.3-4.3-1.3-1.1-3.2-1-4.3.3,0,0,0,0,0,0l-5.6,6.7c-1.1,1.3-1,3.2.3,4.3,0,0,0,0,0,0l6.6,5.8c1.3,1,3.2.9,4.3-.3.5-.6.7-1.4.7-2.2-.1-.8-.5-1.5-1.1-2.1l-1.1-1c9.1.6,16.2,8.1,16.3,17.3.1,4.7-1.6,9.2-4.8,12.6-6.6,6.9-17.6,7.2-24.5.6-3.4-3.2-5.3-7.7-5.4-12.5,0-.8-.4-1.6-1-2.2-.5-.6-1.3-.9-2.1-.8-.8,0-1.6.3-2.1.9-.6.6-.9,1.4-.9,2.2,0,6.3,2.7,12.3,7.2,16.7,4.4,4.3,10.2,6.6,16.3,6.6h.4c6.3-.1,12.2-2.8,16.5-7.3,4.3-4.6,6.6-10.7,6.5-17,0-6.3-2.7-12.3-7.2-16.7Z"/>
    </svg>`;

// Create image elements for each icon
const editImg = document.createElement('img');
editImg.src = editIcon;

const deleteImg = document.createElement('img');
deleteImg.src = deleteIcon;

const rotateImg = document.createElement('img');
rotateImg.src = rotateIcon;

const useCustomControls = (canvas) => {
    const setPlayer = usePlayerStore((state) => state.set);
    const setText = useTextStore((state) => state.set);
    const {frameIndex} = useSheet();
    const allowDelete = frameIndex === 0;
    const clickedControl = useRef('');
    const showInfo = useSnackbarStore((state) => state.show);
    const controlsInitialized = useRef(false);
    const eventHandlersRef = useRef({});

    const {dispatch: onAction} = useAction();

    // Update the ref whenever dependencies change
    eventHandlersRef.current = {
        editControl: (event, transform) => {
            if(!transform) return;
            const {target} = transform;

            if (target && target.custom) {
                if (target.custom.type === 'text') {
                    setText(target);
                }

                if (target.custom.type === 'player') {
                    setPlayer(target);
                }
            } else if (target && target.type === 'group' && target.custom?.type === 'player') {
                // Handle player groups
                setPlayer(target);
            }
        },
        deleteControl: (_event, _transform) => {
            // to prevent accidental delete
            if(clickedControl.current === 'deleteControl') {
                clickedControl.current = '';

                if (!allowDelete) {
                    showInfo('board.sheet.move.no_delete', {severity: 'error'});
                    return;
                }

                onAction('remove');
            }
        },
    };

    // Separate effect for control creation (runs once)
    useEffect(() => {
        // Check if controls are already initialized
        if (controlsInitialized.current) {
            return;
        }

        const initializeControls = async () => {
            // In Fabric v6, controls are accessed via the controls property
            // We need to ensure the controls object exists before adding custom controls
            if (!FabricObject.prototype.controls) {
                // Get default controls from the class if they exist
                FabricObject.prototype.controls = {...(FabricObject.prototype.controls || {})};
            }

            if (!Textbox.prototype.controls) {
                Textbox.prototype.controls = {...(Textbox.prototype.controls || {})};
            }

            customControls.forEach((control) => {
                // Check if control already exists
                if (FabricObject.prototype.controls?.[control]) {
                    return;
                }

                switch (control) {
                case 'editControl': {
                    const img = document.createElement('img');
                    img.src = editIcon;

                    function renderIcon(ctx, left, top, styleOverride, fabricObject) {
                        // Check if this is an editable element
                        const isEditable = editControlElement.includes(fabricObject.custom?.type);
                        
                        // For Groups, check if they are player groups (have custom.type === 'player')
                        // Don't show edit icon for regular shape groups
                        const isPlayerGroup = (fabricObject.type === 'Group' || fabricObject.type === 'group') && 
                                             fabricObject.custom?.type === 'player';
                        
                        if (isEditable || isPlayerGroup) {
                            const size = this.cornerSize;
                            ctx.save();
                            ctx.translate(left, top);
                            // Don't rotate - Fabric.js already handles the object rotation
                            // Just draw the icon upright
                            ctx.drawImage(editImg, -size / 2, -size / 2, size, size);
                            ctx.restore();
                        }
                    }

                    // for custom all will be objects
                    FabricObject.prototype.controls[control] = new Control({
                        x: -0.5,
                        y: -0.5,
                        offsetY: -8,
                        offsetX: -8,
                        cursorStyle: 'pointer',
                        mouseUpHandler: (e, t) => {
                            eventHandlersRef.current[control]?.(e, t);
                        },
                        render: renderIcon,
                        cornerSize: 24,
                        sizeX: 44,
                        sizeY: 44,
                        visible: true,
                    });

                    // for native type we need to add it manually
                    Textbox.prototype.controls[control] = new Control({
                        x: -0.5,
                        y: -0.5,
                        offsetY: -8,
                        offsetX: -8,
                        cursorStyle: 'pointer',
                        mouseUpHandler: (e, t) => {
                            eventHandlersRef.current[control]?.(e, t);
                        },
                        render: renderIcon,
                        cornerSize: 24,
                        sizeX: 44,
                        sizeY: 44,
                        visible: true,
                    });

                    break;
                }

                case 'deleteControl': {
                    function renderIcon(ctx, left, top) {
                        // draw for all controls
                        const size = this.cornerSize;
                        ctx.save();
                        ctx.translate(left, top);
                        // Don't rotate - Fabric.js already handles the object rotation
                        // Just draw the icon upright
                        ctx.drawImage(deleteImg, -size / 2, -size / 2, size, size);
                        ctx.restore();
                    }

                    // for custom all will be objects
                    FabricObject.prototype.controls[control] = new Control({
                        x: 0.5,
                        y: -0.5,
                        offsetY: -8,
                        offsetX: 8,
                        cursorStyle: 'pointer',
                        mouseDownHandler: () => clickedControl.current = control,
                        mouseUpHandler: (e, t) => eventHandlersRef.current[control]?.(e, t),
                        render: renderIcon,
                        cornerSize: 24,
                        sizeX: 44,
                        sizeY: 44,
                    });

                    // for native type we need to add it manually
                    Textbox.prototype.controls[control] = new Control({
                        x: 0.5,
                        y: -0.5,
                        offsetY: -8,
                        offsetX: 8,
                        cursorStyle: 'pointer',
                        mouseDownHandler: () => clickedControl.current = control,
                        mouseUpHandler: (e, t) => eventHandlersRef.current[control]?.(e, t),
                        render: renderIcon,
                        cornerSize: 24,
                        sizeX: 44,
                        sizeY: 44,
                    });

                    break;
                }

                case 'tipControl':
                case 'tailControl': {
                    const isTip = control === 'tipControl';

                    function renderEndpoint(ctx, left, top, _styleOverride, fabricObject) {
                        if (fabricObject.custom?.type !== 'line') return;
                        const size = 14;
                        ctx.save();
                        ctx.translate(left, top);
                        ctx.beginPath();
                        ctx.arc(0, 0, size / 2, 0, 2 * Math.PI);
                        ctx.fillStyle = 'white';
                        ctx.fill();
                        ctx.strokeStyle = '#000';
                        ctx.lineWidth = 2;
                        ctx.stroke();
                        ctx.restore();
                    }

                    FabricObject.prototype.controls[control] = new Control({
                        x: 0,
                        y: 0,
                        cursorStyle: 'crosshair',
                        render: renderEndpoint,
                        cornerSize: 14,
                        sizeX: 28,
                        sizeY: 28,
                        visible: true,
                        positionHandler: (dim, finalMatrix, fabricObject) => {
                            if (fabricObject.custom?.type !== 'line') return new Point(0, 0);
                            const {x1, y1, x2, y2} = fabricObject.custom;
                            let px, py;
                            if (isTip) {
                                // Place handle beyond the arrowhead tip (15px past center in arrow direction)
                                const dx = x2 - x1, dy = y2 - y1;
                                const len = Math.sqrt(dx * dx + dy * dy) || 1;
                                px = x2 + (dx / len) * 25;
                                py = y2 + (dy / len) * 25;
                            } else {
                                px = x1; py = y1;
                            }

                            return new Point(px, py).transform(fabricObject.getViewportTransform());
                        },
                        actionHandler: (eventData, transform, x, y) => {
                            const target = transform.target;
                            if (target.custom?.type !== 'line') return false;
                            const c = target.custom;
                            const {stroke, width, dashed} = c;
                            const [nx1, ny1, nx2, ny2] = isTip
                                ? [c.x1, c.y1, x, y]
                                : [x, y, c.x2, c.y2];
                            // Rebuild children from scene coords — no coordinate space issues
                            const {line, triangle} = buildArrowChildren(nx1, ny1, nx2, ny2, stroke, width, dashed);
                            // Rebuild children — suppress canvas events during swap
                            const cv = target.canvas;
                            if (cv) cv.isLoading = true;
                            target.remove(...target._objects);
                            target.add(line, triangle);
                            if (cv) cv.isLoading = false;
                            // Update group bbox
                            const pad = 20;
                            const bLeft = Math.min(nx1, nx2) - pad;
                            const bTop = Math.min(ny1, ny2) - pad;
                            const bRight = Math.max(nx1, nx2) + pad;
                            const bBottom = Math.max(ny1, ny2) + pad;
                            target.set({
                                width: bRight - bLeft,
                                height: bBottom - bTop,
                                left: (bLeft + bRight) / 2,
                                top: (bTop + bBottom) / 2,
                                dirty: true,
                            });
                            c.x1 = nx1; c.y1 = ny1; c.x2 = nx2; c.y2 = ny2;
                            target.setCoords();
                            return true;
                        },
                        actionName: isTip ? 'moveTip' : 'moveTail',
                    });

                    Textbox.prototype.controls[control] = FabricObject.prototype.controls[control];
                    break;
                }

                case 'cornerA':
                case 'cornerB':
                case 'cornerC': {
                    const cornerKey = control;

                    function renderCorner(ctx, left, top, _styleOverride, fabricObject) {
                        if (fabricObject.custom?.type !== 'triangle') return;
                        const size = 14;
                        ctx.save();
                        ctx.translate(left, top);
                        ctx.beginPath();
                        ctx.arc(0, 0, size / 2, 0, 2 * Math.PI);
                        ctx.fillStyle = 'white';
                        ctx.fill();
                        ctx.strokeStyle = '#000';
                        ctx.lineWidth = 2;
                        ctx.stroke();
                        ctx.restore();
                    }

                    FabricObject.prototype.controls[control] = new Control({
                        x: 0,
                        y: 0,
                        cursorStyle: 'crosshair',
                        render: renderCorner,
                        cornerSize: 14,
                        sizeX: 28,
                        sizeY: 28,
                        visible: true,
                        positionHandler: (dim, finalMatrix, fabricObject) => {
                            if (fabricObject.custom?.type !== 'triangle') return new Point(0, 0);
                            const idx = cornerKey === 'cornerA' ? 0 : cornerKey === 'cornerB' ? 1 : 2;
                            const pt = fabricObject.points[idx];
                            const po = fabricObject.pathOffset;
                            const mat = fabricObject.calcTransformMatrix();
                            const vpt = fabricObject.getViewportTransform();
                            return new Point(pt.x - po.x, pt.y - po.y).transform(mat).transform(vpt);
                        },
                        actionHandler: (eventData, transform, x, y) => {
                            const target = transform.target;
                            if (target.custom?.type !== 'triangle') return false;
                            const c = target.custom;

                            // Move only the dragged corner
                            if (cornerKey === 'cornerA') { c.ax = x; c.ay = y; }
                            else if (cornerKey === 'cornerB') { c.bx = x; c.by = y; }
                            else { c.cx = x; c.cy = y; }

                            // Update absolute point coords and recompute bbox
                            target.points[0] = {x: c.ax, y: c.ay};
                            target.points[1] = {x: c.bx, y: c.by};
                            target.points[2] = {x: c.cx, y: c.cy};
                            target.setBoundingBox();
                            target.set('dirty', true);
                            target.setCoords();

                            return true;
                        },
                        actionName: 'moveCorner',
                    });

                    Textbox.prototype.controls[control] = FabricObject.prototype.controls[control];
                    break;
                }

                default:
                    break;
                }
            });

            // textbox size corner color
            Textbox.prototype.transparentCorners = false;
            Textbox.prototype.cornerSize = 8;

            // Update the mtr (rotation) control to use the rotation icon image
            const renderRotationIcon = function (ctx, left, top) {
                const size = 24; // Size for the icon
                
                ctx.save();
                ctx.translate(left, top);
                // Don't rotate - Fabric.js already handles the object rotation
                // Just draw the icon upright
                ctx.drawImage(rotateImg, -size / 2, -size / 2, size, size);
                ctx.restore();
            };
            
            // Create a custom getVisibility function that always returns true
            const alwaysVisible = function() {
                return true;
            };
            
            // Check if mtr control exists, if not create it
            if (FabricObject.prototype.controls.mtr) {
                FabricObject.prototype.controls.mtr.render = renderRotationIcon;
                FabricObject.prototype.controls.mtr.getVisibility = alwaysVisible;
                FabricObject.prototype.controls.mtr.sizeX = 34;
                FabricObject.prototype.controls.mtr.sizeY = 34;
                FabricObject.prototype.controls.mtr.cornerSize = 24;
                FabricObject.prototype.controls.mtr.visible = true;
                // Position at top middle
                FabricObject.prototype.controls.mtr.x = 0;
                FabricObject.prototype.controls.mtr.y = -0.5;
                FabricObject.prototype.controls.mtr.offsetY = -40;
                FabricObject.prototype.controls.mtr.offsetX = 0;
            } else {
                // Import the rotation action handler from Fabric
                FabricObject.prototype.controls.mtr = new Control({
                    x: 0,
                    y: -0.5,
                    offsetY: -40,
                    offsetX: 0,
                    cursorStyle: 'crosshair',
                    actionHandler: controlsUtils.rotationWithSnapping,
                    actionName: 'rotate',
                    render: renderRotationIcon,
                    getVisibility: alwaysVisible,
                    cornerSize: 24,
                    sizeX: 34,
                    sizeY: 34,
                    visible: true,
                });
            }
            
            // Also update for Textbox
            if (Textbox.prototype.controls.mtr) {
                Textbox.prototype.controls.mtr.render = renderRotationIcon;
                Textbox.prototype.controls.mtr.getVisibility = alwaysVisible;
                Textbox.prototype.controls.mtr.sizeX = 34;
                Textbox.prototype.controls.mtr.sizeY = 34;
                Textbox.prototype.controls.mtr.cornerSize = 24;
                Textbox.prototype.controls.mtr.visible = true;
                // Position at top middle
                Textbox.prototype.controls.mtr.x = 0;
                Textbox.prototype.controls.mtr.y = -0.5;
                Textbox.prototype.controls.mtr.offsetY = -40;
                Textbox.prototype.controls.mtr.offsetX = 0;
            } else {
                Textbox.prototype.controls.mtr = new Control({
                    x: 0,
                    y: -0.5,
                    offsetY: -40,
                    offsetX: 0,
                    cursorStyle: 'crosshair',
                    actionHandler: controlsUtils.rotationWithSnapping,
                    actionName: 'rotate',
                    render: renderRotationIcon,
                    getVisibility: alwaysVisible,
                    cornerSize: 24,
                    sizeX: 34,
                    sizeY: 34,
                    visible: true,
                });
            }

            // Mark controls as initialized
            controlsInitialized.current = true;
        };

        initializeControls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas]); // Only depend on canvas, not eventHandlers

    const updateControls = useCallback(() => {
        // No-op: handlers are now always up-to-date via ref
        // This function is kept for backward compatibility
    }, []);

    return {
        updateControls,
    };
};

export default useCustomControls;